import { LIVE_INPUT_SAMPLE_RATE } from "./live-voice-audio";

export type LiveUserAudioUtterance = Readonly<{
  utteranceId: number;
  audio: Uint8Array;
  mimeType: "audio/wav";
}>;

export const LIVE_INPUT_AUDIO_MAX_BYTES = 8 * 1024 * 1024;
const WAV_HEADER_BYTES = 44;
const SAMPLE_BYTES = 2;
const SILENCE_RMS = 0.035;
const ENDPOINT_SILENCE_SAMPLES = LIVE_INPUT_SAMPLE_RATE * 1.2;
const PRE_ROLL_BYTES = LIVE_INPUT_SAMPLE_RATE * 0.16 * SAMPLE_BYTES;
const MIN_VOICED_SAMPLES = LIVE_INPUT_SAMPLE_RATE * 0.064;

type AudioInputIssue = { utteranceId: number; reason: "unfinished" | "too_long" };
type AudioInputOptions = {
  onUtterance: (input: LiveUserAudioUtterance) => void;
  onStart?: () => void;
  onIncomplete: (input: AudioInputIssue) => void;
};

function rawPcmRms(chunk: Uint8Array): number {
  const samples = chunk.byteLength / SAMPLE_BYTES;
  const view = new DataView(chunk.buffer, chunk.byteOffset, chunk.byteLength);
  let sum = 0;
  for (let index = 0; index < samples; index++) {
    const sample = view.getInt16(index * SAMPLE_BYTES, true) / 0x8000;
    sum += sample * sample;
  }
  return Math.sqrt(sum / samples);
}

function wav(parts: Uint8Array[], bytes: number): Uint8Array {
  const audio = new Uint8Array(WAV_HEADER_BYTES + bytes);
  const view = new DataView(audio.buffer);
  const tag = (offset: number, text: string) => {
    for (let index = 0; index < text.length; index++)
      view.setUint8(offset + index, text.charCodeAt(index));
  };
  tag(0, "RIFF");
  view.setUint32(4, audio.byteLength - 8, true);
  tag(8, "WAVE");
  tag(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, LIVE_INPUT_SAMPLE_RATE, true);
  view.setUint32(28, LIVE_INPUT_SAMPLE_RATE * SAMPLE_BYTES, true);
  view.setUint16(32, SAMPLE_BYTES, true);
  view.setUint16(34, 16, true);
  tag(36, "data");
  view.setUint32(40, bytes, true);
  let offset = WAV_HEADER_BYTES;
  for (const part of parts) {
    audio.set(part, offset);
    offset += part.byteLength;
  }
  return audio;
}

/** One bounded local utterance, never the whole call. Speech boundaries use captured PCM, not model text.
 * Одна обмежена репліка, без запису всієї розмови. Межі визначає звук, а не текст моделі. */
export class LiveVoiceInputAudio {
  private sequence = 0;
  private utteranceId: number | null = null;
  private parts: Uint8Array[] = [];
  private bytes = 0;
  private preRoll: Uint8Array[] = [];
  private preRollBytes = 0;
  private voicedSamples = 0;
  private silenceSamples = 0;
  private discardUntilSilence = false;

  constructor(private readonly options: AudioInputOptions) {}

  get busy(): boolean {
    return this.utteranceId !== null || this.discardUntilSilence;
  }

  push(chunk: Uint8Array): void {
    if (!chunk.byteLength) return;
    if (chunk.byteLength % SAMPLE_BYTES) {
      this.abandon();
      return;
    }
    const samples = chunk.byteLength / SAMPLE_BYTES;
    const voiced = rawPcmRms(chunk) >= SILENCE_RMS;
    if (this.discardUntilSilence) {
      this.silenceSamples = voiced ? 0 : this.silenceSamples + samples;
      if (this.silenceSamples >= ENDPOINT_SILENCE_SAMPLES) {
        this.discardUntilSilence = false;
        this.silenceSamples = 0;
        this.rememberPreRoll(chunk);
      }
      return;
    }
    if (this.utteranceId === null) {
      if (!voiced) {
        this.rememberPreRoll(chunk);
        return;
      }
      this.utteranceId = ++this.sequence;
      this.parts = this.preRoll;
      this.bytes = this.preRollBytes;
      this.preRoll = [];
      this.preRollBytes = 0;
      this.options.onStart?.();
    }
    if (WAV_HEADER_BYTES + this.bytes + chunk.byteLength > LIVE_INPUT_AUDIO_MAX_BYTES) {
      const utteranceId = this.utteranceId;
      this.reset();
      this.discardUntilSilence = true;
      this.options.onIncomplete({ utteranceId, reason: "too_long" });
      return;
    }
    this.parts.push(chunk.slice());
    this.bytes += chunk.byteLength;
    if (voiced) {
      this.voicedSamples += samples;
      this.silenceSamples = 0;
    } else this.silenceSamples += samples;
    if (this.silenceSamples >= ENDPOINT_SILENCE_SAMPLES) this.finish();
  }

  /** An explicit release/close ends the captured recording. / Явне завершення закриває записану репліку. */
  finish(): void {
    const utteranceId = this.utteranceId;
    const audio =
      utteranceId !== null && this.voicedSamples >= MIN_VOICED_SAMPLES
        ? wav(this.parts, this.bytes)
        : null;
    this.reset();
    if (audio && utteranceId !== null)
      this.options.onUtterance(Object.freeze({ utteranceId, audio, mimeType: "audio/wav" }));
  }

  /** A fault is not a release: ignore its resumed tail until silence. / Після збою хвіст відкидається до тиші. */
  abandon(): void {
    const utteranceId = this.utteranceId;
    this.reset();
    this.discardUntilSilence = true;
    if (utteranceId !== null) this.options.onIncomplete({ utteranceId, reason: "unfinished" });
  }

  private rememberPreRoll(chunk: Uint8Array): void {
    this.preRoll.push(chunk.slice(-PRE_ROLL_BYTES));
    this.preRollBytes += Math.min(PRE_ROLL_BYTES, chunk.byteLength);
    while (this.preRollBytes > PRE_ROLL_BYTES) {
      const head = this.preRoll[0]!;
      const excess = this.preRollBytes - PRE_ROLL_BYTES;
      if (head.byteLength <= excess) {
        this.preRoll.shift();
        this.preRollBytes -= head.byteLength;
      } else {
        this.preRoll[0] = head.slice(excess);
        this.preRollBytes -= excess;
      }
    }
  }

  private reset(): void {
    this.utteranceId = null;
    this.parts = [];
    this.bytes = 0;
    this.preRoll = [];
    this.preRollBytes = 0;
    this.voicedSamples = 0;
    this.silenceSamples = 0;
  }
}
